import { forwardRef } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import {
  Block,
  ExternalLink,
  Heading,
  Paragraph,
  SingleSelect,
} from 'suomifi-ui-components'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import {
  selectedSingleItem,
  useCountrySelectData,
} from 'todistukset-common/src/vtj/ui/country-select.util'
import { useAriaOptionsFunction } from 'todistukset-ui/src/vtj/components/order-wizard/country-select.util'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  FormContainer,
  InfoInlineAlert,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { useFirstErrorImperativeHandle } from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { validateUaetPlaceOfMarriage } from 'todistukset-common/src/vtj/uaet-validation'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import { getPropertyTranslationKey } from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import { useUaetFormActions } from 'todistukset-ui/src/vtj/stores/actions/uaet-form-actions'
import {
  isCountryInApostilleByCode,
  isCountryInEUByCode,
} from 'todistukset-common/src/vtj/country.util'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'

const UaetUsageRegionStep: StepComponent = observer(
  forwardRef(function UaetUsageRegionStep({ titleId }, ref) {
    const { t } = useTranslation()
    const {
      steps: {
        [CertificateTypes.UAETTODISTUS]: { showStepValidationErrors },
      },
      common: { lang },
    } = useStore()
    const form = useUaetForm()
    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'placeOfMarriage',
    ])

    const { setPlaceOfMarriage } = useUaetFormActions()
    const { countrySelectList, countrySelectIndex } = useCountrySelectData({
      lang,
    })
    const ariaOptionsFunction = useAriaOptionsFunction()

    const placeOfMarriageValue = finalEditableFieldValue(form.placeOfMarriage)

    const errors = getErrors(form)

    const showLegalizeNotify =
      placeOfMarriageValue &&
      !isCountryInEUByCode(placeOfMarriageValue) &&
      !isCountryInApostilleByCode(placeOfMarriageValue)

    const showFinlandError = placeOfMarriageValue === EuCountries.FINLAND

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardUsageRegionStepMainTitle" />
          </Heading>
        </Block>
        <Block variant="section">
          <SingleSelect
            id="select-country-widget"
            data-test-id="single-select-country"
            labelText={t('placeOfMarriagePrompt')}
            visualPlaceholder={t(
              'orderWizardUsageRegionStepCountrySelectPrompt'
            )}
            items={countrySelectList}
            clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
            noItemsText={t('orderWizardSingleSelectNoMatchingItems')}
            ariaOptionsAvailableTextFunction={ariaOptionsFunction}
            selectedItem={
              selectedSingleItem(placeOfMarriageValue, countrySelectIndex) ??
              null
            }
            onItemSelectionChange={setPlaceOfMarriage}
            status={
              showStepValidationErrors && errors.placeOfMarriage
                ? 'error'
                : 'default'
            }
            statusText={
              showStepValidationErrors && errors.placeOfMarriage
                ? t(
                    getPropertyTranslationKey(errors.placeOfMarriage),
                    errors.placeOfMarriage.errorTranslationParams
                  )
                : undefined
            }
            forwardedRef={refs.placeOfMarriage}
          />

          <Block aria-live="polite">
            {showLegalizeNotify && (
              <InfoInlineAlert
                aria-live="polite"
                labelText={t('orderWizardUaetUsageRegionLegalizeTitle')}
                data-test-id="legalize-info"
              >
                <Paragraph>
                  <LocalizedString stringKey="orderWizardUaetUsageRegionLegalizeBody" />
                </Paragraph>
                <Paragraph>
                  <ExternalLink
                    labelNewWindow={t('opensInNewWindow')}
                    href={t(
                      'orderWizardUaetUsageRegionLegalizeInstructionsLinkHref'
                    )}
                    underline="initial"
                  >
                    <LocalizedString stringKey="orderWizardUaetUsageRegionLegalizeInstructionsLinkText" />
                  </ExternalLink>
                </Paragraph>
              </InfoInlineAlert>
            )}
            {showFinlandError && (
              <InfoInlineAlert
                status="error"
                aria-live="polite"
                labelText={t('orderWizardUaetUsageRegionFinlandErrorTitle')}
              >
                <Paragraph>
                  <LocalizedString stringKey="orderWizardUaetUsageRegionFinlandErrorBody" />
                </Paragraph>
                <Paragraph>
                  <ExternalLink
                    labelNewWindow={t('opensInNewWindow')}
                    href={t('orderWizardUaetUsageRegionFinlandErrorLinkHref')}
                    underline="initial"
                  >
                    <LocalizedString stringKey="orderWizardUaetUsageRegionFinlandErrorLinkText" />
                  </ExternalLink>
                </Paragraph>
              </InfoInlineAlert>
            )}
          </Block>
        </Block>
      </FormContainer>
    )
  })
)

function getErrors(form: UaetUiForm) {
  return {
    placeOfMarriage: validateUaetPlaceOfMarriage(
      form.placeOfMarriage?.ilmoitettuArvo
    ),
  }
}

export default UaetUsageRegionStep
